import type { SurveyStrings } from './i18n';
import type { SurveyConsents, SurveyFigures, SurveyPayload, SurveyWeek } from './report';

/*
 * The campaign's seasons, as every card reads them.
 *
 * The payload carries seasons as `campaign.periods`, each with its weeks, its
 * dates and its own figures. A payload from before seasons (no dates, no
 * figures) still reads: the dates come from the weeks, figures that add up
 * over weeks (plays, tickets, quizzes started, time) are summed, and the
 * distinct counts (visitors, players, consents) are null — "—", never a guess.
 *
 * The header's picker sets the SCOPE: both seasons (the campaign) or one. The
 * headline figures follow the scope; the per-season columns show the seasons
 * in it.
 */

/** Season 1 dark blue, Season 2 teal (design); a third season would be light teal. */
export const SEASON_COLORS = ['#065A8E', '#3BB8C9', '#8ED3DD'];

/**
 * The campaign runs in two seasons (confirmed 2026-10-29). A payload from
 * before the season rework still carries a third period (p3, from 30 Nov)
 * that is no longer part of the campaign: it is not shown.
 */
export const CAMPAIGN_SEASONS = 2;

/** Every figure of a stretch of the campaign, null when it is not known. */
export type Figures = {
  [K in Exclude<keyof SurveyFigures, 'consents'>]: number | null;
} & { consents: SurveyConsents | null };

export type SeasonStatus = 'planned' | 'live' | 'done';

export type Season = {
  id: string;
  /** 1-based: Season 1, Season 2 … */
  n: number;
  start: string;
  end: string;
  weeks: SurveyWeek[];
  /** Null figures for a season that has not started — "—" everywhere, not 0. */
  figures: Figures;
  status: SeasonStatus;
  liveWeek: SurveyWeek | null;
  color: string;
};

/** 'all' = both seasons; otherwise a season id. */
export type Scope = 'all' | string;

const sumOf = (weeks: SurveyWeek[], pick: (w: SurveyWeek) => number | null): number | null => {
  const measured = weeks.map(pick).filter((v): v is number => v != null);
  return measured.length > 0 ? measured.reduce((a, b) => a + b, 0) : null;
};

const NOTHING: Figures = {
  uniqueVisitors: null,
  optIns: null,
  uniquePlayers: null,
  plays: null,
  ticketsIssued: null,
  timeInAppSec: null,
  consents: null,
  quizzesStarted: null,
  playersStarted: null,
};

/** A payload from before seasons: what adds up over weeks, nothing else. */
function figuresFromWeeks(weeks: SurveyWeek[]): Figures {
  return {
    ...NOTHING,
    plays: sumOf(weeks, (w) => w.plays),
    ticketsIssued: sumOf(weeks, (w) => w.ticketsIssued),
    timeInAppSec: sumOf(weeks, (w) => w.timeInAppSec),
    quizzesStarted: sumOf(weeks, (w) => w.quizzesStarted),
  };
}

function statusOf(weeks: SurveyWeek[]): SeasonStatus {
  if (weeks.some((w) => w.status === 'live')) return 'live';
  if (weeks.length > 0 && weeks.every((w) => w.status === 'planned')) return 'planned';
  return weeks.every((w) => w.status === 'done') ? 'done' : 'live';
}

export function seasonsOf(payload: SurveyPayload): Season[] {
  return payload.campaign.periods.slice(0, CAMPAIGN_SEASONS).map((p, i) => {
    const ids = new Set(p.weeks);
    const weeks = payload.weeks.filter((w) => ids.has(w.week));
    const status = statusOf(weeks);
    return {
      id: p.id,
      n: i + 1,
      start: p.start ?? weeks[0]?.start ?? payload.campaign.start,
      end: p.end ?? weeks[weeks.length - 1]?.end ?? payload.campaign.end,
      weeks,
      figures: status === 'planned' ? NOTHING : (p.totals ?? figuresFromWeeks(weeks)),
      status,
      liveWeek: weeks.find((w) => w.status === 'live') ?? null,
      color: SEASON_COLORS[i % SEASON_COLORS.length],
    };
  });
}

/** First and last day of the seasons shown — the campaign as the dashboard draws it. */
export function seasonsSpan(seasons: Season[], campaign: SurveyPayload['campaign']): { start: string; end: string } {
  return { start: seasons[0]?.start ?? campaign.start, end: seasons[seasons.length - 1]?.end ?? campaign.end };
}

/** The seasons a scope shows. */
export const seasonsIn = (seasons: Season[], scope: Scope): Season[] => (scope === 'all' ? seasons : seasons.filter((s) => s.id === scope));

/** The headline figures of a scope: the campaign's (members counted once) or one season's. */
export function figuresOf(payload: SurveyPayload, seasons: Season[], scope: Scope): Figures {
  if (scope === 'all') return payload.totals;
  return seasons.find((s) => s.id === scope)?.figures ?? NOTHING;
}

/** The weeks of a scope, in order. */
export const weeksIn = (seasons: Season[], scope: Scope): SurveyWeek[] => seasonsIn(seasons, scope).flatMap((s) => s.weeks);

/** The season a campaign week belongs to. */
export const seasonOfWeek = (seasons: Season[], week: number): Season | undefined => seasons.find((s) => s.weeks.some((w) => w.week === week));

/** The season a local day belongs to — none for a day between two seasons. */
export const seasonOfDay = (seasons: Season[], date: string): Season | undefined => seasons.find((s) => date >= s.start && date <= s.end);

/** A week's number inside its season: campaign week 5 is Season 2's week 1. */
export function seasonWeekNo(seasons: Season[], week: number): number {
  const s = seasonOfWeek(seasons, week);
  const i = s ? s.weeks.findIndex((w) => w.week === week) : -1;
  return i >= 0 ? i + 1 : week;
}

/** "Week 1" inside a season's own block; "S2 · Week 1" where seasons sit side by side. */
export function weekLabel(seasons: Season[], week: number, t: SurveyStrings, withSeason: boolean): string {
  const n = seasonWeekNo(seasons, week);
  const s = seasonOfWeek(seasons, week);
  return withSeason && s ? t.seasonWeek(s.n, n) : t.week(n);
}

/** Members in both seasons: a season's sum minus the campaign's distinct count. Null unless all three are known. */
export function inBoth(seasons: Season[], total: number | null, pick: (f: Figures) => number | null): number | null {
  if (total == null || seasons.length < 2) return null;
  const parts = seasons.map((s) => pick(s.figures));
  if (parts.some((p) => p == null)) return null;
  return Math.max(0, (parts as number[]).reduce((a, b) => a + b, 0) - total);
}

/** "S1 · ENDED", "S2 · WEEK 2 / 4", "S2 · STARTS 17 NOV". */
export function seasonBadge(s: Season, t: SurveyStrings, fmtDayMonth: (iso: string) => string): string {
  if (s.status === 'done') return t.seasonBadge.ended(s.n);
  if (s.status === 'planned') return t.seasonBadge.starts(s.n, fmtDayMonth(s.start).toUpperCase());
  const k = s.liveWeek ? s.weeks.indexOf(s.liveWeek) + 1 : 1;
  return t.seasonBadge.week(s.n, k, s.weeks.length);
}
